import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Leaf, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "./Register";

const inputCls =
  "w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm text-charcoal placeholder:text-taupe/70 outline-none transition-colors duration-300 focus:border-gold focus:bg-white";

const STAFF_ROLES = ["owner", "admin", "support", "marketing"];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setMember } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const authErrorShown = useRef(false);

  useEffect(() => {
    document.title = "Member Login | Rooted Healing";
    if (location.state?.authError && !authErrorShown.current) {
      authErrorShown.current = true;
      toast.error(location.state.authError);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishLogin = (data) => {
    setMember(data);
    toast.success(`Welcome back, ${data.name.split(" ")[0]}.`);
    navigate(
      location.state?.from ||
        (STAFF_ROLES.includes(data.role) ? "/admin" : "/my-wellness-journey"),
      { replace: true }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setLoading(false);
        toast.success("A security code is on its way to your email.");
        return;
      }
      finishLogin(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const onVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/mfa", {
        mfa_token: mfaToken,
        code: code.trim(),
      });
      finishLogin(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not verify that code."));
      setLoading(false);
    }
  };

  const forgot = async () => {
    if (!form.email.trim()) {
      toast.error("Enter your email address first, then tap forgot password.");
      return;
    }
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      toast.success("If an account exists for this email, a reset link is on its way.");
    } catch {
      toast.error("Could not process that just now. Please try again.");
    }
  };

  return (
    <main data-testid="login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden pt-[76px]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 h-[460px] w-[460px] rounded-full bg-sage/70 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-gold/10 blur-3xl"
      />
      <Reveal className="relative w-full max-w-md px-5 py-16">
        <div className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-9 shadow-[0_25px_60px_rgba(23,63,53,0.12)] backdrop-blur-sm sm:p-11">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sage">
            <Leaf className="h-5 w-5 text-forest" />
          </div>
          <h1 className="mt-6 font-serif text-3xl text-forest">Welcome back</h1>
          <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
            Sign in to continue your wellness journey, revisit your profile, and explore how
            things have changed.
          </p>
          <div className="mt-7">
            <GoogleButton testid="login-google-button" />
            <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-taupe">
              <span className="h-px flex-1 bg-forest/15" />
              or with email
              <span className="h-px flex-1 bg-forest/15" />
            </div>
          </div>
          {mfaToken && (
            <form data-testid="mfa-form" onSubmit={onVerifyCode} className="flex flex-col gap-4">
              <div>
                <label htmlFor="mfa-code" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                  Security code
                </label>
                <p className="mb-3 text-sm leading-relaxed text-charcoal/70">
                  This is a privileged account. We emailed a 6-digit code to {form.email}. Enter
                  it here within 10 minutes to finish signing in.
                </p>
                <input
                  id="mfa-code"
                  data-testid="mfa-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className={`${inputCls} text-center text-lg tracking-[0.4em]`}
                />
              </div>
              <button
                type="submit"
                data-testid="mfa-verify-button"
                disabled={loading || code.length !== 6}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify & Sign In
              </button>
              <button
                type="button"
                data-testid="mfa-back-button"
                onClick={() => {
                  setMfaToken(null);
                  setCode("");
                }}
                className="text-center text-xs font-semibold text-taupe underline-offset-4 hover:underline"
              >
                Back to password
              </button>
            </form>
          )}
          {!mfaToken && (
          <form data-testid="login-form" onSubmit={onSubmit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="login-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                Email address
              </label>
              <input
                id="login-email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="login-password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className={`${inputCls} pr-12`}
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-taupe transition-colors hover:text-forest"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={forgot}
                className="text-xs font-semibold text-forest underline-offset-4 hover:underline"
              >
                Forgot your password?
              </button>
            </div>
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>
          )}
          <p className="mt-7 border-t border-forest/10 pt-6 text-sm text-charcoal/70">
            New to Rooted Wellness?{" "}
            <Link to="/register" data-testid="login-register-link" className="font-semibold text-forest underline-offset-4 hover:underline">
              Create your free account
            </Link>
          </p>
        </div>
      </Reveal>
    </main>
  );
}
